import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { API_URL } from "@/shared/config";
import { useAuth } from "@/app/providers";
import { apiService } from "@/shared/api";

export type AreaPermissions = {
  appointments: boolean;
  meetings: boolean;
  concierge: boolean;
  bar: boolean;
  events: boolean;
  gym: boolean;
  beauty: boolean;
  barOrders: boolean;
  ticketing: boolean;
  faq: boolean;
  services: boolean;
  lending: boolean;
  messaging: boolean;
  assistantIa: boolean;
  externalLinks: {
    id: number;
    label: string;
    description: string;
    url: string;
    icon: string;
  }[];
};

const defaultPerms: AreaPermissions = {
  appointments: false,
  meetings: false,
  concierge: false,
  bar: false,
  events: false,
  gym: false,
  beauty: false,
  barOrders: false,
  ticketing: false,
  faq: false,
  services: false,
  lending: false,
  messaging: false,
  assistantIa: false,
  externalLinks: [],
};

const AREA_CACHE_TTL_MS = 5 * 60 * 1000;
const areaCache = new Map<number, { perms: AreaPermissions; ts: number }>();
const areaInFlight = new Map<number, Promise<AreaPermissions>>();

export type AreaPermissionsContextValue = {
  loading: boolean;
  error: string | null;
  perms: AreaPermissions;
  has: {
    appointments: boolean;
    meetings: boolean;
    concierge: boolean;
    bar: boolean;
    events: boolean;
    gym: boolean;
    beauty: boolean;
    barOrders: boolean;
    ticketing: boolean;
    faq: boolean;
    services: boolean;
    lending: boolean;
    messaging: boolean;
    assistantIa: boolean;
    externalLinks: boolean;
  };
  refresh: () => Promise<void>;
};

const AreaPermissionsContext =
  createContext<AreaPermissionsContextValue | null>(null);

function useAreaPermissionsState(): AreaPermissionsContextValue {
  const { token, currentBuilding } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [perms, setPerms] = useState<AreaPermissions>(defaultPerms);

  const buildingPk = currentBuilding?.pk ? Number(currentBuilding.pk) : null;

  const fetchArea = useCallback(async () => {
    if (!token || !buildingPk) return;

    const cached = areaCache.get(buildingPk);
    const now = Date.now();
    if (cached && now - cached.ts < AREA_CACHE_TTL_MS) {
      setPerms(cached.perms);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const existing = areaInFlight.get(buildingPk);
      if (existing) {
        const next = await existing;
        setPerms(next);
        return;
      }

      const promise = (async () => {
        const url = `${API_URL}/area/crudArea/${buildingPk}/`;
        const response: any = await apiService.get(url, token);
        const areaRaw = Array.isArray(response?.fields)
          ? response.fields[0]
          : response?.fields || {};
        const area: Record<string, any> = {};
        Object.keys(areaRaw || {}).forEach((k) => {
          const normalized = k
            .normalize("NFD")
            .replace(/\p{Diacritic}/gu, "")
            .toLowerCase();
          area[normalized] = (areaRaw as any)[k];
        });

        const next: AreaPermissions = {
          appointments: !!(area["liste_de_rdv"] ?? area["liste_derdv"]),
          meetings: !!(area["reservation"] ?? area["réservation"]),
          concierge: !!area["conciergerie"],
          bar: !!area["bar"],
          events: !!(area["evenement"] ?? area["évènement"]),
          gym: !!(
            area["salle_de_sport"] ??
            area["salle_de_sport".normalize("NFD")]
          ),
          beauty: !!(area["beautyroom"] ?? area["beauty_room"]),
          barOrders: !!(area["commande_bar"] ?? area["commande-bar"]),
          ticketing: !!area["signalement"],
          faq: !!area["faq"],
          services: !!area["services"],
          lending: !!(area["prets"] ?? area["pret"]),
          // Messagerie existait avant le flag Area : champ absent → visible.
          // Une fois messagerie explicitement false en base, on masque.
          messaging: (() => {
            const raw = area["messagerie"] ?? area["messaging"];
            return raw === undefined ? true : !!raw;
          })(),
          assistantIa: (() => {
            const raw =
              area["assistant_ia"] ??
              area["assistantia"] ??
              area["assistant_ia".normalize("NFD")];
            return raw === undefined ? true : !!raw;
          })(),
          externalLinks: Array.isArray(areaRaw?.external_links)
            ? areaRaw.external_links
            : [],
        };
        areaCache.set(buildingPk, { perms: next, ts: Date.now() });
        return next;
      })();

      areaInFlight.set(buildingPk, promise);
      try {
        const next = await promise;
        setPerms(next);
      } finally {
        areaInFlight.delete(buildingPk);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load permissions");
      setPerms(defaultPerms);
    } finally {
      setLoading(false);
    }
  }, [token, buildingPk]);

  useEffect(() => {
    if (!token || !buildingPk) {
      setLoading(false);
      setError(null);
      setPerms(defaultPerms);
      return;
    }
    void fetchArea();
  }, [token, buildingPk, fetchArea]);

  const has = useMemo(
    () => ({
      appointments: perms.appointments,
      meetings: perms.meetings,
      concierge: perms.concierge,
      bar: perms.bar,
      events: perms.events,
      gym: perms.gym,
      beauty: perms.beauty,
      barOrders: perms.barOrders,
      ticketing: perms.ticketing,
      faq: perms.faq,
      services: perms.services,
      lending: perms.lending,
      messaging: perms.messaging,
      assistantIa: perms.assistantIa,
      externalLinks: perms.externalLinks.length > 0,
    }),
    [perms],
  );

  return { loading, error, perms, has, refresh: fetchArea } as const;
}

export function AreaPermissionsProvider({ children }: { children: ReactNode }) {
  const value = useAreaPermissionsState();
  return (
    <AreaPermissionsContext.Provider value={value}>
      {children}
    </AreaPermissionsContext.Provider>
  );
}

export function useAreaPermissions(): AreaPermissionsContextValue {
  const ctx = useContext(AreaPermissionsContext);
  if (!ctx) {
    throw new Error(
      "useAreaPermissions must be used within AreaPermissionsProvider",
    );
  }
  return ctx;
}
